import type { Dispatch } from 'react';

import type { GameEvent } from '../../types/game';
import type { GameAction } from '../gameReducer';
import { debugLog } from './debugLog';

/**
 * Skąd przyszedł dispatch. Bez tego dwa wpisy APPLY_EVENT o tym samym zdarzeniu
 * (optymistyczny i zatwierdzony) są nie do odróżnienia w śladzie.
 */
export type ApplyEventSource = 'optimistic' | 'committed' | 'online' | 'local';

const shortId = (value?: string | null): string | null => (value ? value.slice(0, 8) : null);

const num = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Maska kości jako `10110` – tablica booleanów zajmowałaby pół wiersza śladu. */
const mask = (value?: boolean[] | null): string | null =>
  Array.isArray(value) ? value.map((kept) => (kept ? '1' : '0')).join('') : null;

const readPlayerId = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') return null;
  const candidate = (payload as Record<string, unknown>).playerId;
  return typeof candidate === 'string' ? candidate : null;
};

/**
 * Opis zdarzenia na potrzeby śladu debugowego.
 *
 * Samo `{id, type, turn}` nie wystarczyło do sprawdzenia punktacji rozegranej gry –
 * każde rozliczenie trzeba było dociągać z bazy. Dlatego ślad niesie gracza, deltę
 * i wynik po rozliczeniu, czyli komplet potrzebny do odtworzenia tablicy z samego
 * pliku NDJSON. Payload zostaje płaski i krótki: to jeden wiersz na zdarzenie,
 * a gra botów potrafi ich wygenerować tysiąc.
 */
export function describeApplyEvent(event: GameEvent): Record<string, unknown> {
  const base = {
    id: shortId(event.id),
    type: event.type,
    turn: event.turn,
  };

  switch (event.type) {
    case 'SCORE':
      return {
        ...base,
        player: shortId(event.payload.playerId),
        delta: num(event.payload.delta),
        basePoints: num(event.payload.basePoints),
        resultingScore: num(event.payload.resultingScore),
      };
    case 'PENALTY':
      return {
        ...base,
        player: shortId(event.payload.playerId),
        delta: num(event.payload.delta),
        reason: event.payload.reason ?? null,
        resultingScore: num(event.payload.resultingScore),
      };
    case 'OVERTAKE':
      return {
        ...base,
        player: shortId(event.payload.overtakerId),
        bonus: num(event.payload.bonus),
        victims: (event.payload.penalties || []).map((victim) => `${shortId(victim.playerId)}:${num(victim.delta)}`),
      };
    case 'ZONE_TRIGGERED': {
      const payload = event.payload;
      return {
        ...base,
        player: shortId(payload.playerId),
        zoneId: payload.zoneId,
        kind: payload.kind,
        tier: payload.tier,
        zoneDelta: num(payload.zoneDelta),
        overtakeBonus: num(payload.overtakeBonus),
        triggerScore: num(payload.triggerScore),
        resultingScore: num(payload.resultingScore),
        links: payload.links?.length ?? 1,
        settledIndex: payload.settledIndex ?? null,
        loop: payload.loop ? `${payload.loop.startIndex}-${payload.loop.endIndex}` : null,
        victims: (payload.penalties || []).map((victim) => `${shortId(victim.playerId)}:${num(victim.delta)}`),
      };
    }
    case 'TURN_END':
      return {
        ...base,
        player: shortId(event.payload.playerId),
        basePoints: num(event.payload.basePoints),
        finalDice: event.payload.finalDice ?? null,
        overshotWinScore: event.payload.overshotWinScore === true,
      };
    case 'DICE_ROLL':
      return {
        ...base,
        player: shortId(event.payload.playerId),
        rollIndex: event.payload.rollIndex,
        dice: event.payload.dice,
        kept: mask(event.payload.keptMask),
        hotDiceReset: event.payload.isHotDiceReset === true,
      };
    case 'DICE_KEEP':
      return {
        ...base,
        player: shortId(event.payload.playerId),
        rollIndex: event.payload.rollIndex,
        kept: mask(event.payload.keptMask),
      };
    case 'ADD_PLAYER':
      return {
        ...base,
        player: shortId(event.payload.playerId),
        name: event.payload.name,
        role: event.payload.role ?? 'player',
        isBot: event.payload.isBot === true,
      };
    case 'GAME_CREATED':
      return { ...base, mode: event.payload.mode ?? null };
    case 'GAME_STARTED':
      return { ...base, player: shortId(event.payload.firstPlayerId) };
    case 'ORDER_FINALIZED':
      return { ...base, order: (event.payload.order || []).map(shortId) };
    case 'PLAYER_LEFT':
      return { ...base, player: shortId(event.payload.playerId), reason: event.payload.reason ?? null };
    default:
      return { ...base, player: shortId(readPlayerId(event.payload)) };
  }
}

/**
 * Jedyna droga do `APPLY_EVENT` z klienta. Ślad powstaje przy dispatchu, a nie w reducerze:
 * reducer zostaje czysty, a `React.StrictMode` i replay po `ROLLBACK_EVENT` przestają
 * dublować wpisy, bo nic ich już nie emituje w środku.
 */
export function dispatchApplyEvent(
  dispatch: Dispatch<GameAction>,
  event: GameEvent,
  source: ApplyEventSource,
  extra?: Record<string, unknown>,
): void {
  debugLog('APPLY_EVENT', { source, ...describeApplyEvent(event), ...(extra || {}) }, { source: 'applyEvent' });
  dispatch({ type: 'APPLY_EVENT', event });
}
